import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
    useGetAdminConsentItems,
    useGetAdminConsentItemVersions,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { type Locale } from "src/api/locales/useLocales";
import { ConsentDocumentPreviewDialog } from "src/components/Consent/ConsentDocumentPreviewDialog";
import { getConsentVersionStatus } from "./consentVersionStatus";

type ConsentPreviewDialogProps = {
    item?: ConsentGovernanceItem;
    locale?: string;
    localeOptions?: Array<Locale & { code: string }>;
    onClose: () => void;
    open: boolean;
    version?: ConsentGovernanceVersion;
};

const getConsentItemLabel = (item?: ConsentGovernanceItem, version?: ConsentGovernanceVersion) => {
    if (item?.name) {
        return item.name;
    }

    if (typeof version?.consentItem === "object" && version.consentItem !== null) {
        return version.consentItem.name ?? version.consentItem.code ?? String(version.consentItem.id);
    }

    if (typeof version?.consentItem === "string") {
        return version.consentItem.split("/").filter(Boolean).at(-1) ?? version.consentItem;
    }

    return "";
};

const getConsentItemId = (version?: ConsentGovernanceVersion) => {
    if (typeof version?.consentItem === "object" && version.consentItem !== null) {
        return version.consentItem.id;
    }

    return undefined;
};

export const ConsentPreviewDialog = ({
    item,
    locale,
    localeOptions = [],
    onClose,
    open,
    version,
}: ConsentPreviewDialogProps) => {
    const { t } = useTranslation();
    const defaultLocale = useMemo(() => localeOptions.find((option) => option.isDefault)?.code, [localeOptions]);
    const isDefaultLocale = !locale || !defaultLocale || locale === defaultLocale;
    const consentItemId = item?.id ?? getConsentItemId(version);
    const consentItemCode =
        item?.code ??
        (typeof version?.consentItem === "object" && version.consentItem !== null ?
            version.consentItem.code
        :   undefined);

    const { consentItems: translatedNameItems = [] } = useGetAdminConsentItems({
        enabled: open && !isDefaultLocale && consentItemCode !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            code: consentItemCode,
            contentLocale: locale,
            contentLocaleFields: "name",
        },
    });
    const { consentItems: translatedDescriptionItems = [] } = useGetAdminConsentItems({
        enabled: open && !isDefaultLocale && consentItemCode !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            code: consentItemCode,
            contentLocale: locale,
            contentLocaleFields: "description",
        },
    });
    const { consentItemVersions: translatedTitleVersions = [] } = useGetAdminConsentItemVersions({
        enabled: open && !isDefaultLocale && version !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            "consentItem.id": consentItemId,
            contentLocale: locale,
            contentLocaleFields: "title",
            version: version?.version,
        },
    });
    const { consentItemVersions: translatedSummaryVersions = [] } = useGetAdminConsentItemVersions({
        enabled: open && !isDefaultLocale && version !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            "consentItem.id": consentItemId,
            contentLocale: locale,
            contentLocaleFields: "summary",
            version: version?.version,
        },
    });
    const { consentItemVersions: translatedContentVersions = [] } = useGetAdminConsentItemVersions({
        enabled: open && !isDefaultLocale && version !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            "consentItem.id": consentItemId,
            contentLocale: locale,
            contentLocaleFields: "content",
            version: version?.version,
        },
    });
    const { consentItemVersions: translatedDocumentUrlVersions = [] } = useGetAdminConsentItemVersions({
        enabled: open && !isDefaultLocale && version !== undefined,
        itemsPerPage: 200,
        locale,
        queryParams: {
            "consentItem.id": consentItemId,
            contentLocale: locale,
            contentLocaleFields: "documentUrl",
            version: version?.version,
        },
    });

    const hasTranslatedItemField = (items: ConsentGovernanceItem[]) =>
        consentItemId !== undefined && items.some((candidate) => candidate.id === consentItemId);
    const hasTranslatedVersionField = (versions: ConsentGovernanceVersion[]) =>
        version !== undefined && versions.some((candidate) => candidate.id === version.id);
    const hasLocalizedName = isDefaultLocale || (item !== undefined && hasTranslatedItemField(translatedNameItems));
    const hasLocalizedDescription =
        item !== undefined && (isDefaultLocale || hasTranslatedItemField(translatedDescriptionItems));
    const hasLocalizedTitle =
        version !== undefined && (isDefaultLocale || hasTranslatedVersionField(translatedTitleVersions));
    const hasLocalizedSummary =
        version !== undefined && (isDefaultLocale || hasTranslatedVersionField(translatedSummaryVersions));
    const hasLocalizedContent =
        version !== undefined && (isDefaultLocale || hasTranslatedVersionField(translatedContentVersions));
    const hasLocalizedDocumentUrl =
        version !== undefined && (isDefaultLocale || hasTranslatedVersionField(translatedDocumentUrlVersions));

    const itemLabel = hasLocalizedName ? getConsentItemLabel(item, version) : "";
    const title =
        hasLocalizedTitle && version?.title ? version.title
        : hasLocalizedName && item?.name ? item.name
        : t("settings.consent.preview.title", "Preview");
    const description = hasLocalizedDescription ? item?.description : null;
    const summary = hasLocalizedSummary ? version?.summary : null;
    const content = hasLocalizedContent ? version?.content : null;
    const documentUrl = hasLocalizedDocumentUrl ? version?.documentUrl : null;
    const status = version ? getConsentVersionStatus(version) : null;
    const statusTag =
        status === "archived" ?
            {
                label: t("settings.consent.version.status_archived", "Archived"),
                variant: "tags-grey" as const,
            }
        : status === "published" ?
            {
                label: t("settings.consent.version.status_published", "Published"),
                variant: "tags-green" as const,
            }
        : status === "draft" ?
            {
                label: t("settings.consent.version.status_draft", "Draft"),
                variant: "tags-grey" as const,
            }
        :   undefined;

    return (
        <ConsentDocumentPreviewDialog
            open={open}
            onClose={onClose}
            title={title}
            itemLabel={itemLabel}
            version={version?.version}
            description={description}
            summary={summary}
            content={content}
            documentUrl={documentUrl}
            statusTag={statusTag}
            showDescription
        />
    );
};
